import { appOrigin, consoleOrigin } from "@/shared/config/consoleHost";

/**
 * Passagem da sessão de suporte do console para o app.
 *
 * Console e app são origens diferentes (storage separado), então o token de suporte não pode
 * ser escrito pelo console no storage do app. O console abre `<app>/suporte` numa aba nova;
 * a página avisa que está pronta e o console responde com a sessão. Os dois lados conferem a
 * origem: o token só sai para o app e o app só aceita o que vem do console.
 */
export const SUPPORT_HANDOFF_PATH = "/suporte";

/** Tempo para a aba nova carregar e pedir a sessão. */
const HANDOFF_TIMEOUT_MS = 60_000;
/** O app repete o pedido: o console só escuta depois de o impersonate responder. */
const READY_INTERVAL_MS = 500;

const READY = "7days:support-handoff:ready";
const SESSION = "7days:support-handoff:session";

export type SupportHandoffSession = {
    accessToken: string;
    expiresInSeconds: number;
    targetLabel: string;
    tenantSlug: string;
    /** Página do console para onde voltar no fim. */
    returnUrl: string;
};

type SessionMessage = { type: typeof SESSION; session: SupportHandoffSession };

function isSessionMessage(value: unknown): value is SessionMessage {
    if (!value || typeof value !== "object") return false;
    const message = value as Partial<SessionMessage>;
    const session = message.session as Partial<SupportHandoffSession> | undefined;
    return (
        message.type === SESSION &&
        typeof session?.accessToken === "string" &&
        typeof session.expiresInSeconds === "number" &&
        typeof session.targetLabel === "string" &&
        typeof session.tenantSlug === "string" &&
        typeof session.returnUrl === "string"
    );
}

/**
 * Lado do console. Abre a aba do app já no clique (depois de um `await` o browser bloqueia o
 * popup) e devolve a função que entrega a sessão assim que ela existir.
 */
export function openSupportHandoffTab(): { deliver: (session: SupportHandoffSession) => Promise<void>; cancel: () => void } {
    const target = appOrigin();
    const tab = window.open(`${target}${SUPPORT_HANDOFF_PATH}`, "_blank");

    const cancel = () => tab?.close();

    const deliver = (session: SupportHandoffSession) =>
        new Promise<void>((resolve, reject) => {
            if (!tab) {
                reject(new Error("O browser bloqueou a aba da sessão de suporte."));
                return;
            }
            const timer = window.setTimeout(() => {
                window.removeEventListener("message", onMessage);
                reject(new Error("A aba da sessão de suporte não respondeu."));
            }, HANDOFF_TIMEOUT_MS);

            function onMessage(event: MessageEvent) {
                if (event.origin !== target || event.source !== tab) return;
                const data = event.data as { type?: unknown } | null;
                if (data?.type !== READY) return;
                window.clearTimeout(timer);
                window.removeEventListener("message", onMessage);
                tab?.postMessage({ type: SESSION, session } satisfies SessionMessage, target);
                resolve();
            }

            window.addEventListener("message", onMessage);
        });

    return { deliver, cancel };
}

/** Lado do app: pede a sessão a quem abriu a aba e devolve-a só se vier do console. */
export function receiveSupportHandoff(): Promise<SupportHandoffSession> {
    const source = consoleOrigin();
    const opener = window.opener as Window | null;

    return new Promise((resolve, reject) => {
        if (!opener) {
            reject(new Error("Esta página só abre a partir do console."));
            return;
        }
        const askAgain = window.setInterval(() => opener.postMessage({ type: READY }, source), READY_INTERVAL_MS);
        const stop = () => {
            window.clearInterval(askAgain);
            window.clearTimeout(timer);
            window.removeEventListener("message", onMessage);
        };
        const timer = window.setTimeout(() => {
            stop();
            reject(new Error("O console não enviou a sessão de suporte."));
        }, HANDOFF_TIMEOUT_MS);

        function onMessage(event: MessageEvent) {
            if (event.origin !== source || event.source !== opener || !isSessionMessage(event.data)) return;
            stop();
            resolve(event.data.session);
        }

        window.addEventListener("message", onMessage);
        opener.postMessage({ type: READY }, source);
    });
}
